import React, { useState } from "react";
import { useContact } from "../../../hooks/useContact";
import { API_ENDPOINTS } from "../../../constants/api";

const FALLBACK_CONTACT = {
  name: "Drop A Line",
  description:
    "Ready to join our AI literacy journey? We'd love to hear from you. Connect with AILH to explore innovative AI education opportunities.",
  phone_number: "+852 2358 8888",
  address: "HKUST Campus\nClear Water Bay, Hong Kong",
  email: "ailh@hkust.edu.hk",
};

const formatTelHref = (phone?: string | null) =>
  phone ? `tel:${phone.replace(/\s+/g, "")}` : undefined;

const Contact: React.FC = () => {
  const { contact } = useContact();
  const contactInfo = contact || FALLBACK_CONTACT;
  const addressLines = contactInfo.address
    ? contactInfo.address.split("\n")
    : [];

  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formValues.name) newErrors.name = "Name is required";
    if (!formValues.email) newErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(formValues.email))
      newErrors.email = "Invalid email";
    if (!formValues.message) newErrors.message = "Message is required";
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.formSubmission, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formValues),
      });
      if (response.ok) {
        setSuccess(true);
        setFormValues({ name: "", email: "", message: "" });
      } else {
        setSubmitError("Something went wrong. Please try again.");
      }
    } catch (err: any) {
      setSubmitError(err?.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <section id="contact-us" className="contact-one">
      <div className="container">
        <div className="section-title text-center">
          <div className="subtitle">[ Get In Touch ]</div>
          <h2>Contact AILH</h2>
        </div>

        <div className="row">
          <div className="col-xl-5 col-lg-6">
            <div className="contact-one__left">
              <div className="contact-one__left-content">
                <div className="contact-one__title-box">
                  <h2>{contactInfo.name || FALLBACK_CONTACT.name}</h2>
                  <p>
                    {contactInfo.description || FALLBACK_CONTACT.description}
                  </p>
                </div>
                <ul className="contact-one__contact-list">
                  <li>
                    <div className="icon">
                      <i className="fas fa-phone"></i>
                    </div>
                    <div className="text">
                      <p>
                        {contactInfo.phone_number ? (
                          <a href={formatTelHref(contactInfo.phone_number)}>
                            {contactInfo.phone_number}
                          </a>
                        ) : (
                          FALLBACK_CONTACT.phone_number
                        )}
                      </p>
                    </div>
                  </li>
                  <li>
                    <div className="icon">
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <div className="text">
                      <p>
                        {addressLines.length
                          ? addressLines.map((line, index) => (
                              <React.Fragment key={`${line}-${index}`}>
                                {line}
                                {index !== addressLines.length - 1 && <br />}
                              </React.Fragment>
                            ))
                          : "HKUST Campus\nClear Water Bay, Hong Kong"}
                      </p>
                    </div>
                  </li>
                  <li>
                    <div className="icon">
                      <i className="fas fa-envelope"></i>
                    </div>
                    <div className="text">
                      <p>
                        <a
                          href={`mailto:${
                            contactInfo.email || FALLBACK_CONTACT.email
                          }`}
                        >
                          {contactInfo.email || FALLBACK_CONTACT.email}
                        </a>
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="col-xl-7 col-lg-6">
            <div className="contact-one__right">
              <div className="contact-one__form-box">
                <h2 className="contact-one__form-title">Send us a message</h2>
                {success && (
                  <p className="text-success">Form submitted successfully!</p>
                )}
                {submitError && <p className="text-danger">{submitError}</p>}

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <p className="contact-one__form-label">Name*</p>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formValues.name}
                      onChange={handleChange}
                      required
                    />
                    <div className="contact-one__form-icon">
                      <i className="fas fa-user"></i>
                    </div>
                    {errors.name && (
                      <p className="text-danger">{errors.name}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <p className="contact-one__form-label">Email*</p>
                    <input
                      type="email"
                      name="email"
                      placeholder="your.email@example.com"
                      value={formValues.email}
                      onChange={handleChange}
                      required
                    />
                    <div className="contact-one__form-icon">
                      <i className="far fa-envelope"></i>
                    </div>
                    {errors.email && (
                      <p className="text-danger">{errors.email}</p>
                    )}
                  </div>

                  <div className="form-group text-message-box">
                    <p className="contact-one__form-label">How can we help?*</p>
                    <textarea
                      name="message"
                      placeholder="Enter your message here"
                      value={formValues.message}
                      onChange={handleChange}
                      required
                    />
                    {errors.message && (
                      <p className="text-danger">{errors.message}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <div className="button-box">
                      <button
                        type="submit"
                        className="thm-btn contact-one__btn"
                      >
                        Submit Request
                      </button>
                    </div>
                  </div>
                </form>

                <p className="ajax-response mb-0"></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
